import { register, run } from "./registry";

// A restored report contains new nodes; a serialized data attribute cannot track bindings.
const boundRows = new WeakSet<HTMLTableRowElement>();

register((root) => {
  const searchInput = root.querySelector<HTMLInputElement>("[data-kit-list-search]");
  const table = root.querySelector<HTMLTableElement>("table[data-kit-list]");
  if (!table && !searchInput) return;

  if (searchInput && table) {
    // the table is replaced by a server search, so look it up each time
    const currentTable = () => root.querySelector<HTMLTableElement>("table[data-kit-list]") ?? table;
    let timer = 0;
    let latest = 0;
    searchInput.addEventListener("input", () => {
      const q = searchInput.value.toLowerCase().trim();
      const rows = currentTable().querySelectorAll<HTMLTableRowElement>("tbody tr");
      rows.forEach((r) => {
        const text = r.textContent?.toLowerCase() || "";
        r.style.display = text.includes(q) ? "" : "none";
      });
      // Owner 2026-10-30: the filter above sees only this page; after a pause, search the whole list on the server.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void serverSearch(++latest), 400);
    });

    const serverSearch = async (ticket: number) => {
      const form = searchInput.form;
      if (!form || searchInput.value.trim() === (searchInput.dataset.kitQ ?? "").trim()) return;
      const data = new FormData(form);
      data.set("page", "1");
      const url = `${location.pathname}?${new URLSearchParams(data as unknown as Record<string, string>).toString()}`;
      const res = await fetch(url, { headers: { Accept: "text/html" } });
      if (!res.ok || ticket !== latest) return;   // a newer search is on its way
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const card = currentTable().closest(".cb-list-card");
      for (const sel of [".table-responsive", ".cb-card-footer"]) {
        const fresh = doc.querySelector(sel);
        const old = card?.querySelector(sel);
        if (fresh && old) {
          old.replaceWith(fresh);
          run(fresh);
        }
      }
      searchInput.dataset.kitQ = searchInput.value;
      history.replaceState(null, "", url);
    };

    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        // A new search term goes to the server (the whole list, not just this page); the same term opens the first row.
        if (searchInput.form && searchInput.value.trim() !== (searchInput.dataset.kitQ ?? "").trim()) {
          searchInput.form.requestSubmit();
          return;
        }
        const firstRow = currentTable().querySelector<HTMLTableRowElement>("tbody tr:not([style*='display: none'])");
        if (firstRow) firstRow.focus();
        return;
      }
      if (e.key === "ArrowDown") {
        const firstRow = currentTable().querySelector<HTMLTableRowElement>("tbody tr:not([style*='display: none'])");
        if (firstRow) {
          e.preventDefault();
          firstRow.focus();
        }
      }
    });
  }

  if (table) {
    const rows = table.querySelectorAll<HTMLTableRowElement>("tbody tr");
    rows.forEach((row) => {
      if (boundRows.has(row)) return;
      boundRows.add(row);
      const open = () => {
        const href = row.getAttribute("data-href");
        // Old report snapshots can still carry the row-wide new-tab flag.
        // Document navigation belongs to the reference anchor only.
        if (!href || row.dataset.kitOpenTab === "1") return;
        location.assign(href);
      };
      row.addEventListener("click", (e) => {
        if (e.target instanceof Element && e.target.closest(".cb-action-cell, a, button, input, select, textarea, label")) return;
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || !window.getSelection()?.isCollapsed) return;
        open();
      });

      row.addEventListener("keydown", (e) => {
        if (e.target !== row) return; // a field inside the row (e.g. a cancel reason) keeps its own keys
        if (/^[1-9]$/.test(e.key) && !e.ctrlKey && !e.altKey) {
          const links = Array.from(table.querySelectorAll<HTMLTableCellElement>("thead th"))
            .map((th) => th.querySelector<HTMLAnchorElement>("a[data-kit-sort]"));
          const a = links[Number(e.key) - 1];
          if (a) {
            e.preventDefault();
            location.assign(a.href);
          }
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          open();
          return;
        }

        if (e.key === "ArrowDown") {
          const next = row.nextElementSibling as HTMLElement | null;
          if (next) {
            e.preventDefault();
            next.focus();
          }
          return;
        }

        if (e.key === "ArrowUp") {
          const prev = row.previousElementSibling as HTMLElement | null;
          if (prev) {
            e.preventDefault();
            prev.focus();
          } else if (searchInput) {
            e.preventDefault();
            searchInput.focus();
          }
          return;
        }
      });
    });
  }
});

register((root) => {
  const deleteRows = root.querySelectorAll<HTMLTableRowElement>("tr[data-kit-delete]");
  deleteRows.forEach((row) => {
    row.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "Delete") {
        e.preventDefault();
        const confirmMsg = row.getAttribute("data-kit-delete-confirm");
        if (!confirmMsg) return;

        let box = document.getElementById("kit-delete-confirm");
        if (!box) {
          box = document.createElement("div");
          box.id = "kit-delete-confirm";
          box.className = "alert alert-warning my-2";
          const table = row.closest("table");
          table?.before(box);
        }
        box.textContent = confirmMsg;
        box.hidden = false;

        const onKey = (ev: KeyboardEvent) => {
          if (ev.key === "y" || ev.key === "Y") {
            ev.preventDefault();
            window.removeEventListener("keydown", onKey, true);
            box!.hidden = true;
            const url = row.getAttribute("data-kit-delete");
            if (!url) return;
            const hxTarget = row.closest("[hx-target]")?.getAttribute("hx-target") ?? "#manifest-rows";
            const htmx = (window as unknown as { htmx?: { ajax: (verb: string, path: string, opt: unknown) => void } }).htmx;
            if (htmx) {
              htmx.ajax("POST", url, { target: hxTarget });
            } else {
              fetch(url, { method: "POST" });
            }
          } else if (ev.key === "n" || ev.key === "N" || ev.key === "Escape") {
            ev.preventDefault();
            window.removeEventListener("keydown", onKey, true);
            box!.hidden = true;
          }
        };
        window.addEventListener("keydown", onKey, true);
      }
    });
  });
});

// WP50: while a search, filter, sort or page link loads the next list, the rows turn into skeletons (ui-lists.css).
register((root) => {
  const table = document.querySelector<HTMLTableElement>("table[data-kit-list]");
  if (root !== document || !table) return; // once per page load, not again after each htmx swap
  const busy = () => table.setAttribute("aria-busy", "true");
  document.addEventListener("submit", (e) => { if ((e.target as HTMLFormElement).method === "get" && !e.defaultPrevented) busy(); });
  document.addEventListener("click", (e) => {
    const a = (e.target as Element).closest?.("a[data-kit-sort], .cb-pagination a");
    if (a && !(e as MouseEvent).ctrlKey && !(e as MouseEvent).metaKey) busy();
  });
  // Back/forward restores the page from cache with the skeleton still on.
  window.addEventListener("pageshow", () => table.removeAttribute("aria-busy"));
});

// Print ▾ All rows reloads the list unpaged with print=1; print once it is on screen.
register(() => {
  const url = new URL(location.href);
  if (url.searchParams.get("print") !== "1" || !document.querySelector("table[data-kit-list]")) return;
  url.searchParams.delete("print");
  history.replaceState(null, "", url);
  if (document.readyState === "complete") setTimeout(() => window.print(), 0);
  else window.addEventListener("load", () => window.print(), { once: true });
});
